import { useMemo } from 'react'
import { Document } from '@/types/documents'
import useGetValidationDocDataQueries from '@/services/hooks/useGetValidationDocDataQueries'
import { sortBySortOrder } from '@/utils/field'
import { useDocumentChipsContext } from './providers/DocumentChipsProvider'
import { useDocumentRowsContext } from './providers/DocumentRowsProvider'
import { useDocumentRowValuesContext } from './providers/DocumentRowValuesProvider'
import { ComparisonModeOptions } from './ComparisonView'

type UseDocumentComparisonDataOptions = {
  enabled: boolean
  mode: ComparisonModeOptions
  mainDocument?: Document
  secondaryDocumentId: string
}

type GroupedData<T> = {
  [x: string]: {
    fieldKey: string
    data: T[]
    fieldId: string
    rowId: string
  }
}

function groupDataByFieldAndRow<
  T extends {
    project_grid_field_id: string | null
    document_row_id: string | null
  },
>(data: T[], rowIndexMap: Record<string, number>) {
  return data.reduce<GroupedData<T>>((acc, item) => {
    if (!item.project_grid_field_id || !item.document_row_id) return acc
    const fieldId = item.project_grid_field_id
    const rowId = item.document_row_id
    const rowIndex = rowIndexMap[rowId]
    const key = `${fieldId}_${rowIndex}`
    if (!acc[key]) {
      acc[key] = {
        fieldKey: `${fieldId}_${rowId}`,
        data: [item],
        fieldId,
        rowId,
      }
    } else {
      acc[key].data.push(item)
    }
    return acc
  }, {})
}

const differentFieldKeysComparingRowValuesForAll = {
  fieldIds: new Set(['all']),
}

export default function useDocumentComparisonData({
  enabled,
  mode,
  mainDocument,
  secondaryDocumentId,
}: UseDocumentComparisonDataOptions) {
  const { documentChips: mainDocumentChips } = useDocumentChipsContext()
  const { documentRows: mainDocumentRows } = useDocumentRowsContext()
  const { documentRowValues: mainDocumentRowValues } =
    useDocumentRowValuesContext()

  const secondaryDocumentDataQueries = useGetValidationDocDataQueries({
    documentId: secondaryDocumentId,
    onlyBasicData: true,
    enabled: enabled && !!mainDocument,
  })

  const {
    documentQuery: secondaryDocumentQuery,
    documentChipsQuery: secondaryDocumentChipsQuery,
    documentRowsQuery: secondaryDocumentRowsQuery,
    documentRowValuesQuery: secondaryDocumentRowValuesQuery,
  } = secondaryDocumentDataQueries

  const secondaryDocument = secondaryDocumentQuery.document
  const secondaryDocumentChips = secondaryDocumentChipsQuery.documentChips
  const secondaryDocumentRows = secondaryDocumentRowsQuery.documentRows
  const secondaryDocumentRowValues =
    secondaryDocumentRowValuesQuery.documentRowValues

  const mainDocumentRowIndexMap = sortBySortOrder(mainDocumentRows).reduce(
    (acc, row, index) => {
      acc[row.id] = index
      return acc
    },
    {},
  )

  const secondaryDocumentRowIndexMap = sortBySortOrder(
    secondaryDocumentRows,
  ).reduce((acc, row, index) => {
    acc[row.id] = index
    return acc
  }, {})

  const differentFieldKeysComparingChips = useMemo(() => {
    if (mode !== 'image' || !secondaryDocument) return undefined
    if (!secondaryDocumentChips.length || !mainDocumentChips.length) {
      return ['all']
    }

    const groupedMainChipsByFieldAndRow = groupDataByFieldAndRow(
      mainDocumentChips,
      mainDocumentRowIndexMap,
    )

    if (Object.keys(groupedMainChipsByFieldAndRow).length === 0) return ['all']

    const groupedSecondaryChipsByFieldAndRows = groupDataByFieldAndRow(
      secondaryDocumentChips,
      secondaryDocumentRowIndexMap,
    )

    const differentFields: string[] = []
    Object.entries(groupedMainChipsByFieldAndRow).forEach(
      ([key, { fieldKey, data: mainChips }]) => {
        const secondaryGroup = groupedSecondaryChipsByFieldAndRows[key]
        if (!secondaryGroup) {
          differentFields.push(fieldKey)
        } else if (secondaryGroup.data.length !== mainChips.length) {
          differentFields.push(fieldKey, secondaryGroup.fieldKey)
        } else {
          const secondaryChips = secondaryGroup.data
          const mainChipsText = mainChips.map((chip) => chip._text).sort()
          const secondaryChipsText = secondaryChips
            .map((chip) => chip._text)
            .sort()
          if (
            !mainChipsText.every(
              (value, index) => value === secondaryChipsText[index],
            )
          ) {
            differentFields.push(fieldKey, secondaryGroup.fieldKey)
          }
        }
      },
    )

    Object.keys(groupedSecondaryChipsByFieldAndRows).forEach((key) => {
      // If the field is not in the main document, it's a different field
      if (!groupedMainChipsByFieldAndRow[key]) {
        differentFields.push(groupedSecondaryChipsByFieldAndRows[key].fieldKey)
      }
    })

    return differentFields
  }, [
    mainDocumentChips,
    mainDocumentRowIndexMap,
    mode,
    secondaryDocument,
    secondaryDocumentChips,
    secondaryDocumentRowIndexMap,
  ])

  const differentFieldsAnsRowsComparingRowValues = useMemo(() => {
    if (mode == 'image' || !secondaryDocument) return undefined

    if (!mainDocumentRowValues.length)
      return differentFieldKeysComparingRowValuesForAll

    if (!secondaryDocumentRowValues.length)
      return differentFieldKeysComparingRowValuesForAll

    const groupedMainRowValuesByFieldAndRow = groupDataByFieldAndRow(
      mainDocumentRowValues,
      mainDocumentRowIndexMap,
    )

    if (Object.keys(groupedMainRowValuesByFieldAndRow).length === 0)
      return differentFieldKeysComparingRowValuesForAll

    const groupedSecondaryRowValuesByFieldAndRows = groupDataByFieldAndRow(
      secondaryDocumentRowValues,
      secondaryDocumentRowIndexMap,
    )

    const differentFields: {
      fieldIds: Set<string>
      rowIds: Set<string>
    } = { fieldIds: new Set(), rowIds: new Set() }

    Object.entries(groupedMainRowValuesByFieldAndRow).forEach(
      ([key, { fieldId, rowId, data: mainRowValues }]) => {
        const mainRowValue = mainRowValues[0]
        const mainDocumentFinalRefId = mainRowValue.final_ref_id
        const secondaryGroup = groupedSecondaryRowValuesByFieldAndRows[key]
        const secondaryRowValue = secondaryGroup?.data[0]

        let isDifferent = false
        if (
          !secondaryRowValue ||
          (mainDocumentFinalRefId &&
            mainDocumentFinalRefId !== secondaryRowValue.final_ref_id) ||
          (!mainDocumentFinalRefId &&
            mainRowValue.final_value !== secondaryRowValue.final_value)
        ) {
          isDifferent = true
        }

        if (isDifferent) {
          differentFields.rowIds.add(rowId)
          differentFields.fieldIds.add(fieldId)
          if (secondaryRowValue) {
            differentFields.rowIds.add(secondaryGroup.rowId)
          }
        }
      },
    )

    Object.keys(groupedSecondaryRowValuesByFieldAndRows).forEach((key) => {
      // If the field is not in the main document, it's a different field
      if (!groupedMainRowValuesByFieldAndRow[key]) {
        differentFields.fieldIds.add(
          groupedSecondaryRowValuesByFieldAndRows[key].fieldId,
        )
        differentFields.rowIds.add(
          groupedSecondaryRowValuesByFieldAndRows[key].rowId,
        )
      }
    })

    return differentFields
  }, [
    mainDocumentRowIndexMap,
    mainDocumentRowValues,
    mode,
    secondaryDocument,
    secondaryDocumentRowIndexMap,
    secondaryDocumentRowValues,
  ])

  const rowsCountByGridId = useMemo(() => {
    if (mode == 'image' || !secondaryDocument) return undefined

    const rowsByGridId: Record<string, Record<string, number>> = {}
    mainDocumentRows.forEach((row) => {
      if (rowsByGridId[row.project_grid_id]) {
        rowsByGridId[row.project_grid_id].main =
          rowsByGridId[row.project_grid_id].main + 1
      } else {
        rowsByGridId[row.project_grid_id] = { main: 1, secondary: 0 }
      }
    })

    secondaryDocumentRows.forEach((row) => {
      if (rowsByGridId[row.project_grid_id]) {
        rowsByGridId[row.project_grid_id].secondary =
          rowsByGridId[row.project_grid_id].secondary + 1
      } else {
        rowsByGridId[row.project_grid_id] = { main: 0, secondary: 1 }
      }
    })

    return rowsByGridId
  }, [mainDocumentRows, mode, secondaryDocument, secondaryDocumentRows])

  return {
    secondaryDocumentQuery,
    secondaryDocumentChipsQuery,
    secondaryDocumentRowValuesQuery,
    secondaryDocumentRowsQuery,
    // We use field keys (fieldId_rowId) for chips since we highlight each row field (image view), which
    // is different for row values (fields view) where we highlight all columns in a table row if one of
    // the values is different.
    differentFieldKeysComparingChips,
    differentFieldsAnsRowsComparingRowValues,
    rowsCountByGridId,
  }
}
